import { useSearchParams } from 'react-router-dom';
import { GeneralSettingsCard } from '@/components/marketing/settings/GeneralSettingsCard';
import { SuppressionsTable } from '@/components/marketing/settings/SuppressionsTable';
import { EmptyState, PageHeader } from '@/components/ui';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { useAuth } from '@/lib/use-auth';

/*
 * /marketing/settings — two sections, `?tab=` in the URL with the default
 * (general) spelled by the key's ABSENCE: nothing is written on arrival, and
 * switching back to General deletes the key. Every write is `{replace: true}`.
 *
 * ⚠️ The ROUTE carries no guard, so a reader without
 * `marketing-settings.read` meets the refusal below and issues NO request.
 */

type Tab = 'general' | 'suppressions';

const TABS: { value: Tab; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'suppressions', label: 'Suppressions' },
];

/** A positive test with a fallback — an unrecognised value lands on General. */
function parseTab(raw: string | null): Tab {
  return raw === 'suppressions' ? 'suppressions' : 'general';
}

export function MarketingSettingsPage() {
  const { ability } = useAuth();
  const canRead = ability.canRead('marketing-settings');
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));

  function setTab(next: Tab) {
    const params = new URLSearchParams(searchParams);
    if (next === 'general') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  }

  if (!canRead) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader eyebrow="B2B Marketing" title="B2B Marketing settings" />
        <EmptyState
          title="Marketing settings are restricted"
          description="Your permission set does not include this. An administrator can change that."
        />
      </div>
    );
  }

  return (
    <div className="grid min-w-0 gap-4">
      <PageHeader eyebrow="B2B Marketing" title="B2B Marketing settings" />

      <ViewToggle
        label="Marketing settings section"
        value={tab}
        options={TABS}
        onChange={setTab}
        className="self-start justify-self-start"
      />

      {tab === 'general' ? <GeneralSettingsCard /> : <SuppressionsTable />}
    </div>
  );
}
